import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout as LayoutIcon, LogOut, ChevronDown, User, CreditCard, Database } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useLayout } from '../../context/LayoutContext';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import OnboardingBanner from './OnboardingBanner';
import MarkdownModal from '../common/MarkdownModal';

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { headerContent, setHeaderContent } = useLayout();
  const isWorkspacePage = location.pathname.startsWith('/workspace/');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex h-screen bg-white dark:bg-gray-950 transition-colors duration-300 overflow-hidden">
      {!isWorkspacePage && <Sidebar />}

      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Top User Bar - Only visible in Workspace */}
        {isWorkspacePage && (
          <header className="h-14 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 flex items-center justify-between px-4 sticky top-0 z-50 transition-colors shrink-0">
            <div className="flex items-center gap-4 flex-1 min-w-0 h-full">
              <button
                onClick={() => {
                  setHeaderContent(null);
                  navigate('/');
                }}
                className="flex items-center group shrink-0 relative z-[60] border-none bg-transparent cursor-pointer p-0 ml-2"
                title="Go to Dashboard"
              >
                <div className="group-hover:scale-110 transition-transform">
                  <img src="/logo.svg" alt="manteo.ai" className="h-7" />
                </div>
              </button>

              {headerContent && (
                <div className="flex-1 flex items-center h-full min-w-0">
                  {headerContent}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={cn(
                    "flex items-center gap-3 pl-2 pr-2 py-1 rounded-xl transition-all group border border-transparent",
                    isUserMenuOpen ? "bg-gray-50 dark:bg-gray-900 border-gray-100 dark:border-gray-800" : "hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-gray-100 dark:border-gray-800"
                  )}
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white font-black text-xs shadow-lg shadow-emerald-200/50 relative overflow-hidden">
                    {user?.email?.split('@')[0]?.substring(0, 2).toUpperCase() || 'GU'}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  </div>
                  <ChevronDown size={14} className={cn("text-gray-400 transition-transform duration-200", isUserMenuOpen && "rotate-180")} />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-2xl py-2 z-50 animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-4 py-3 border-b border-gray-50 dark:border-gray-900/50">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Account</p>
                      <p className="text-xs font-bold text-gray-900 dark:text-white truncate">{user?.email || 'guest@example.com'}</p>
                    </div>

                    <div className="p-1">
                      <Link
                        to="/workspaces"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 text-xs font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-xl transition-colors uppercase tracking-tight"
                      >
                        <LayoutIcon size={16} />
                        Workspaces
                      </Link>
                      <Link
                        to="/sources"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 text-xs font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-xl transition-colors uppercase tracking-tight"
                      >
                        <Database size={16} />
                        Data Sources
                      </Link>
                      <div className="h-px bg-gray-50 dark:bg-gray-900/50 my-1 mx-2" />
                      <Link
                        to="/profile"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 text-xs font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-xl transition-colors uppercase tracking-tight"
                      >
                        <User size={16} />
                        Profile Info
                      </Link>
                      <Link
                        to="/billing"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 text-xs font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-xl transition-colors uppercase tracking-tight"
                      >
                        <CreditCard size={16} />
                        Billing
                      </Link>
                    </div>

                    <div className="px-1 border-t border-gray-50 dark:border-gray-900/50 mt-1 pt-1">
                      <button
                        onClick={() => {
                          logout();
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl transition-colors uppercase tracking-tight"
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </div>

                    <div className="px-1 border-t border-gray-50 dark:border-gray-900/50 mt-1 pt-2">
                      <div className="px-3 py-2">
                        <p className="text-[14px] text-gray-400 dark:text-gray-500 text-center">
                          <button
                            onClick={() => {
                              setTermsModalOpen(true);
                              setIsUserMenuOpen(false);
                            }}
                            className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
                          >
                            Terms
                          </button>
                          {' · '}
                          <button
                            onClick={() => {
                              setPrivacyModalOpen(true);
                              setIsUserMenuOpen(false);
                            }}
                            className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
                          >
                            Privacy
                          </button>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>
        )}

        {location.pathname === '/' && <OnboardingBanner refreshKey={`${location.pathname}${location.search}`} />}

        <main className="flex-1 flex flex-col min-h-0 overflow-y-auto">
          {children}
        </main>

        <MarkdownModal
          isOpen={termsModalOpen}
          onClose={() => setTermsModalOpen(false)}
          markdownUrl="/terms-of-service.md"
        />

        <MarkdownModal
          isOpen={privacyModalOpen}
          onClose={() => setPrivacyModalOpen(false)}
          markdownUrl="/privacy-policy.md"
        />
      </div>
    </div>
  );
};

export default AppLayout;
